import { useContext, useState } from "react";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { AppContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import ShowAlert from "@/hooks/show-alert";

export type ProjectOverviewRow = {
    workstreamName: string
    workstreamId?: number
    rating: 0 | 1 | 2
    source: 0 | 2 | 4 // 0 = Risk, 2 = Issue, 4 = Support Request
    description: string
    id: number
}

export interface OverviewProps {
    projectOverviewRows: ProjectOverviewRow[]
    isMultiProject?: boolean
    demoMode?: boolean;
}

function OverviewComponent({ projectOverviewRows, isMultiProject = false, demoMode = false }: OverviewProps) {
    const app = useContext(AppContext);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState({ title: "", message: "" });

    function getColourFromRating(rating: 0 | 1 | 2) {
        if (rating === 2) {
            return "bg-chart-red";
        }

        if (rating === 1) {
            return "bg-chart-amber";
        }

        return "bg-chart-green";
    }

    function getSourceString(source: 0 | 2 | 4) {
        if (source === 0) {
            return "Risk";
        }
        if (source === 2) {
            return "Issue";
        }
        return "Support"
    }

    function getLocationString(source: 0 | 2 | 4) {
        if (source === 0) {
            return "/Risks";
        }
        if (source === 2) {
            return "/Issues";
        }
        return "/SupportRequests"
    }

    function getIdColumnName(source: 0 | 2 | 4) {
        if (source === 0) {
            return "riskId";
        }
        if (source === 2) {
            return "issueId";
        }
        return "supportRequestId"
    }

    function handleClick(row: ProjectOverviewRow) {
        if (demoMode) {
            setAlertMessage({ title: "Demo Mode", message: "When not in demo mode, this will navigate to the appropriate page." });
            setShowAlert(true);
            return;
        }

        if (isMultiProject) {
            // In multi-project mode, we set the selected project to the one associated with the row
            app.setSelectedProject(app.projects.find(project => project.id === row.workstreamId)!);
        }
        navigate(getLocationString(row.source), { state: [{ id: getIdColumnName(row.source), value: row.id }] });
    }

    return (
        <Card id="overviewCard" className={`flex grow flex-col px-4 pb-2 ${isMobile ? 'max-h-[500px]' : 'max-h-[320px]'} overflow-y-auto scrollbar`}>
            <CardHeader className="items-center p-2">
                <CardTitle className={isMobile ? "text-4xl" : "text-xl"}>{isMultiProject ? "Overview" : app.selectedProject?.name}</CardTitle>
            </CardHeader>
            <div className={`flex flex-col p-2 border border-slate-400 rounded-xl ${isMobile ? 'max-h-[420px]' : 'max-h-[280px]'} overflow-y-auto scrollbar`}>
                {projectOverviewRows.map((row) =>
                    <div className={`overviewCardRow flex flex-row ${isMobile ? 'min-h-24' : 'min-h-12'} py-1 hover: cursor-pointer`} key={row.source + "_" + row.id} onClick={() => { handleClick(row) }}>
                        <div className={`flex flex-col text-center bold w-[25%] justify-center px-2 ${isMobile ? 'text-2xl' : ''}`}>{row.workstreamName}</div>
                        <div className={`flex flex-col text-center text-white justify-center w-[15%] ${isMobile ? 'text-2xl' : ''} ` + getColourFromRating(row.rating) + " hover:shadow-[0_0_10px_5px_rgba(255,255,255,0.5)] hover:brightness-110}"}>{getSourceString(row.source)}</div>
                        <div className={`flex flex-col w-[60%] text-left px-2 overflow-hidden ${isMobile ? 'text-xl' : 'text-sm'}`}>{row.description}</div>
                    </div>
                )}
            </div>
            <ShowAlert
                title={alertMessage.title}
                message={alertMessage.message}
                dialogOpen={showAlert}
                setDialogOpen={setShowAlert}
            />
        </Card>
    );
}

export default OverviewComponent;